import { Overlay }       from "@mantine/core";
import { useDisclosure } from "@mantine/hooks";

import { useGetDeviceType, useSetLoginPopup } from "_common/hooks/global";
import useGetDownloadLink                     from "_common/hooks/useGetDownloadLink";
import { useOpenProModal }                    from "_common/hooks/useOpenProModal";
import { PrimaryButton, SecondaryButton }     from "_components/atoms/button";
import Icon                                   from "_components/atoms/icon";
import Image                                  from "_components/atoms/image";
import Render                                 from "_components/atoms/render";
import Typography                             from "_components/atoms/typography";
import QRModal                                from "_components/molecules/qrMenuModal";
import { SNG_DOWNLOAD_LINK }                  from "_config/index";
import { useSetAdWatchStatus }                from "_jotai/rooterPro";

import useStyles from "./styles";

export const LOGIN_WALL = "loginWall";
export const DUPLICATE_STREAM_WALL = "duplicateStreamWall";
export const WATCH_AD_WALL = "watchAdWall";
export const BUY_PRO_WALL = "buyProWall";
export const PRO_EXPIRED_WALL = "proExpiredWall";
export const DOWNLOAD_APP_WALL = "downloadAppWall";

function component ( {
	browserBlocked,
	canWatchMoreContent,
	background,
	children,
	isReel = false,
	zIndex,
	type
} ) {

	const setLoginModal        = useSetLoginPopup ();
	const { classes, cx }      = useStyles ( { isReel, zIndex }, { name: "login-wall" } );
	const openProModal         = useOpenProModal ();
	const setAdWatchStatus     = useSetAdWatchStatus ();
	const [ opened, handlers ] = useDisclosure ( false );
	const deviceType           = useGetDeviceType ();
	const isMobile             = deviceType === "mobile";

	const overlayDetails = {
		[LOGIN_WALL]: {
			title        : null,
			subtitle     : "Login to continue watching",
			btnEndIcon   : null,
			btnStartIcon : null,
			btnClassName : classes.btn,
			btnTitle     : "Login / Sign Up",
			onClick      : () => setLoginModal ( true )
		},
		[DUPLICATE_STREAM_WALL]: {
			title     : "Stream switched to another device.",
			subtitle  : "Please stop playing on other device and then try here.",
			ctaButton : null
		},
		[WATCH_AD_WALL]: {
			title        : "Free preview ended",
			subtitle     : "Watch AD to continue watching",
			btnEndIcon   : "caret",
			btnClassName : cx ( classes.buyProButton, classes.btn ),
			onClick      : () => setAdWatchStatus ( p => ( { ...p, opened: true } ) ),
			btnStartIcon : null,
			btnTitle     : "Watch Ad"
		},
		[DOWNLOAD_APP_WALL]: {
			title        : "Free preview ended",
			subtitle     : "Buy Pro to watch here or watch for free on the app.",
			downloadApp  : true,
			btnEndIcon   : "caret",
			btnClassName : cx ( classes.buyProButton, classes.btn ),
			onClick      : openProModal,
			btnStartIcon : "proShineWhite",
			btnTitle     : "Buy Pro"
		},
		[BUY_PRO_WALL]: {
			title        : "Free preview ended",
			subtitle     : "Buy Pro to continue watching.",
			btnEndIcon   : "caret",
			btnClassName : cx ( classes.buyProButton, classes.btn ),
			onClick      : openProModal,
			btnStartIcon : "proShineWhite",
			btnTitle     : "Buy Pro"
		},
		[PRO_EXPIRED_WALL]: {
			title        : "Pro Expired",
			subtitle     : "Buy Pro to continue watching.",
			btnClassName : cx ( classes.buyProButton, classes.btn ),
			onClick      : openProModal,
			btnStartIcon : "proShineWhite",
			btnTitle     : "Buy Pro"
		}
	};
	const downloadLink   = useGetDownloadLink ();

	const onClickDownload = () => {
		if ( isMobile )
			return window.open ( downloadLink );

		return handlers.open ();
	};

	if ( browserBlocked )
		return (
			<div className = { classes.loginWallWrapper }>
				<div className = { classes.overlayOuter }>
					<Image
						className = { classes.wallBackground }
						src       = { background }
					/>

					<Overlay
						blur       = { 15 }
						classNames = { { root: classes.overlayRoot } }
					>
						<div className = { classes.overlayInner }>
							<Typography
								className = { classes.text2 }
								title     = "Browser Not Supported"
							/>

							<Typography
								className = { classes.text }
								title     = "Your browser does not support this player. Please use another browser, such as Chrome, Firefox, or Safari."
							/>

						</div>
					</Overlay>
				</div>
			</div>

		);

	return (
		<div className = { classes.loginWallWrapper }>
			<Render condition = { !canWatchMoreContent }>
				<div className = { classes.overlayOuter }>
					<Image
						className = { classes.wallBackground }
						src       = { background }
					/>

					<QRModal
						handleClose = { handlers.close }
						open        = { opened }
					/>

					<Overlay
						blur       = { 15 }
						classNames = { { root: classes.overlayRoot } }
					>
						<div className = { classes.overlayInner }>
							<Typography
								className = { classes.text2 }
								title     = { overlayDetails[type]?.title }
							/>

							<Typography
								className = { classes.text }
								title     = { overlayDetails?.[type]?.subtitle }
							/>

							<div className = { classes.btnContainer }>

								<PrimaryButton
									buttonTextClassName = { classes.btnText }
									className           = { overlayDetails?.[type]?.btnClassName }
									id                  = { "watchAdButton" }
									leftSection         = { (
										<Render condition = { overlayDetails?.[type]?.btnStartIcon }>
											<Icon sameInBothTheme
												name = { overlayDetails?.[type]?.btnStartIcon }
												size = { 15 }
											/>
										</Render>
									) }
									onClick             = { overlayDetails?.[type]?.onClick }
									rightSection        = { (
										<Render condition = { overlayDetails?.[type]?.btnEndIcon }>
											<Icon
												modeType = "dark"
												name     = { overlayDetails?.[type]?.btnEndIcon }
												size     = { 15 }
											/>
										</Render>
									) }
									title               = { overlayDetails?.[type]?.btnTitle }
								/>

								<Render condition = { overlayDetails?.[type]?.downloadApp }>
									<SecondaryButton
										className = { classes.downloadBtn }
										onClick   = { onClickDownload }
										size      = "small"
										title     = { "Download App" }
									/>
								</Render>
							</div>

						</div>
					</Overlay>
				</div>
			</Render>

			<Render condition = { canWatchMoreContent }>
				{ children }
			</Render>
		</div>
	);
}

export default component;
